/**
 * Paleta Cartivia — TEMA ESCURO, alinhado ao site institucional.
 *
 * Cores medidas em cartivia.izap.dev (custom properties do CSS do tema):
 *   #101828  gray-900       — fundo dominante do site
 *   #1e2939  gray-800       — superficie (cards)
 *   #364153  gray-700       — borda / superficie elevada
 *   #43d0a3  cartivia-mint  — acento, gradiente, texto de marca sobre escuro
 *   #7eddbf  mint-soft
 *   #b8eada  mint-pale
 *   #2aad87  green-500      — verde de acao (identico ao `secondary` que o app ja usava)
 *   #1c6d54  emerald-dark   — verde mais escuro publicado pelo site; vira a chrome
 *   #bcb2ff  lilac          — 3a serie de grafico
 *   #1b1e29  brand-navy
 *
 * ============================================================================
 * POR QUE ALGUNS TOKENS FORAM DIVIDIDOS
 * ============================================================================
 * O app nao tem ThemeProvider: os 112 arquivos consomem `colors` direto, e o
 * PAPEL de cada cor esta implicito no call site. No tema claro alguns tokens
 * serviam a dois papeis opostos sem conflito; no escuro eles se separam.
 *
 *  - `primary` era verde escuro em 28 fundos de chrome E em 68 textos sobre
 *    card branco. No escuro, fundo tem de continuar escuro e texto tem de
 *    clarear. Fundo fica em `primary`; texto migrou para `primaryText`.
 *  - `error` era vermelho em 23 textos e em 5 fundos de pilula. Texto migrou
 *    para `errorText` (#ff8a8a rende 6,4:1 sobre a superficie; #FF3333 so
 *    rende 4,0:1 e reprovaria AA).
 *  - `white` era superficie de card em ~123 sites e texto sobre cor em ~55.
 *    Superficie migrou para `surface`; `white` segue sendo branco literal.
 *
 * A escala `grey*` NAO foi dividida: a auditoria mostrou que ela ja era
 * consistente por papel (grey50/100/200 sempre fundo, grey300/400 sempre
 * borda, grey500..900 sempre texto), entao bastou inverter os valores. As
 * unicas excecoes eram as 8 ocorrencias de `grey100` como TEXTO na tela de
 * login — migradas para `white`.
 *
 * MARCA x SEMANTICA: `secondary` (#2aad87) sinaliza acao e status positivo;
 * `primary` (#1c6d54) e a cor de chrome e de botao primario. A leitura de
 * EM_ALTA/EM_BAIXA continua apoiada no par `secondaryText`/`errorText`.
 *
 * A familia `azul*` manteve o nome por compatibilidade (28 call sites), mas os
 * valores sao verdes/neutros da marca — renomear nao traria ganho visual.
 */
export const colors = {
  // ---------------------------------------------------------------- marca --
  /**
   * CHROME — sidebar, top bar, avatar. Azul escuro (brand-navy do site).
   * O verde deixou de ser a cor principal e virou ACENTO: botao de acao,
   * status, destaque e grafico usam `secondary`/`primaryText`.
   * Nao serve como fundo de botao sobre card escuro (1,1:1) — use `secondary`.
   */
  primary: '#1b1e29',
  /**
   * Verde de marca em TEXTO sobre superficie escura (7,5:1 sobre #1e2939).
   * Substitui `primary` nos 68 call sites que eram texto. Nao usar como fundo
   * com texto branco — #43d0a3 rende so 1,9:1 com branco por cima.
   */
  primaryText: '#43d0a3',
  secondary: '#2aad87',
  /** Verde claro para TEXTO — status positivo, EM_ALTA (9,0:1 sobre #1e2939). */
  secondaryText: '#7eddbf',
  /**
   * Verde de FUNDO de botao/chip de acao. Mais escuro que `secondary` de
   * proposito: com #2aad87 o texto BRANCO rende 2,8:1 e reprova AA. Este
   * rende 4,5:1 com branco e 3,2:1 contra o cartao `surface` — os dois pisos.
   * `secondary` continua sendo o verde de PRIMEIRO PLANO (texto, icone,
   * gauge, trilho do switch), onde e mais claro justamente por isso.
   */
  accentBg: '#15866a',
  /**
   * Verde claríssimo (mint-pale do site) para texto pequeno SOBRE a chrome
   * verde `primary`. `secondary` ali rende so 2,2:1 e `secondaryText` 3,9:1;
   * este rende 4,7:1. Usado no destaque da tagline da marca.
   */
  mintPale: '#b8eada',

  // ----------------------------------------------------------- superficie --
  /** Card, top bar, modal, painel. Antes era `white`. */
  surface: '#1e2939',
  /** Elevado: chip, quadrado de icone, thead de tabela secundaria. */
  surfaceHigh: '#364153',

  // ---------------------------------------------------------------- estado --
  /** Vermelho de FUNDO (pilula, botao destrutivo). Escurecido de #FF3333 para
   * #d32f2f: com o original o texto branco por cima dava 3,6:1 e reprovava AA. */
  error: '#d32f2f',
  /** Vermelho de TEXTO sobre superficie escura (6,4:1). EM_BAIXA. */
  errorText: '#ff8a8a',
  /** Estado NEUTRO da predicao. */
  system: '#99a1af',
  help: '#fbbf57',

  /** Fundos translucidos das pilulas de status. */
  lightGreen: 'rgb(42 173 135 / 0.20)',
  lightRed: 'rgb(255 51 51 / 0.16)',
  lightBlue: 'rgb(153 161 175 / 0.16)',

  // --------------------------------------------------------------- neutros --
  white: '#fff',
  black: '#000000',
  /**
   * Escala INVERTIDA em relacao ao tema claro: 50/100/200 sao fundos,
   * 300/400 sao bordas, 500..900 sao textos (900 = mais claro).
   */
  grey50: '#18222f',
  grey100: '#101828',
  grey200: '#26344a',
  grey300: '#364153',
  grey400: '#55637a',
  grey500: '#99a1af',
  grey600: '#afbccc',
  grey700: '#c3cedc',
  grey800: '#dce5ef',
  grey900: '#ffffff',

  // ------------------------------------------------------ familia `azul*` --
  /** Circulo do HeaderCard. */
  azul1: '#26344a',
  /** thead das mini-tabelas e chips de filtro (texto). */
  azul2: '#7eddbf',
  /** Sem consumidor: os cards do Dashboard passaram a `surfaceHigh`. */
  azul3: '#124231',
  /**
   * Trilha dos gauges do Dashboard, sobre os cards `azul3`.
   * Precisa ser HEX: o ApexCharts nao renderiza o arco com valor em `rgb()`.
   */
  trackGrafico: '#101828',
  /** Item de menu ativo — mint translucido. */
  azulOpaco: 'rgb(67 208 163 / 0.28)',

  // ----------------------------------------------------------- utilitarios --
  lilas: '#bcb2ff',
  mask1: '#ff000000',
  overlay: 'rgb(0 0 0 / 0.6)',
  overlay1: 'rgb(0 0 0 / 0.0)',
  /**
   * Cartao das telas de autenticacao (login, esqueci a senha, alterar senha).
   * Azul escuro da marca (gray-800 do site), nao mais o verde da chrome. O
   * fundo DOS INPUTS nao vem daqui: ver Input.style.tsx, que usa um tom mais
   * fundo para o campo nao se dissolver no cartao.
   */
  login: '#1e2939',
  /**
   * Gradiente assinatura do site (mint -> emerald-dark), o mesmo do botao
   * "Agendar demo" de cartivia.izap.dev. SEM CONSUMIDOR hoje: o token ja
   * existia sem uso no tema claro (guardava um degrade azul-marinho legado)
   * e aqui so teve o valor realinhado a marca. Texto por cima exige um tom escuro, nao branco.
   */
  degrade: 'linear-gradient(90deg, #43d0a3, #1c6d54)',
}
